import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicCampaignService } from '../services/campaign/publicCampaignService';
import AdVisualBackground from '../components/AdVisualBackground';
import { getTypographyStyle } from '../utils/campaignPreviewUtils';

// Helper to construct typography object matching Step 4 structure
const buildTypographyObject = (adContent: any) => ({
  selectedFont: adContent.selected_font || adContent.headline_font_family || 'Inter, sans-serif',
  headlineFontSize: parseFloat(adContent.headline_font_size) || 24,
  headlineColor: adContent.headline_color,
  headlineAlign: adContent.headline_align || 'center',
  headlineBold: adContent.headline_bold || false,
  headlineItalic: adContent.headline_italic || false,
  headlineShadow: adContent.headline_shadow || false,

  benefitFontSize: parseFloat(adContent.benefit_font_size) || 18,
  benefitColor: adContent.benefit_color,
  benefitAlign: adContent.benefit_align || 'center',
  benefitBold: adContent.benefit_bold || false,
  benefitItalic: adContent.benefit_italic || false,
  benefitShadow: adContent.benefit_shadow || false,

  descriptionFontSize: parseFloat(adContent.description_font_size) || 16,
  descriptionColor: adContent.description_color,
  descriptionAlign: adContent.description_align || 'center',
  descriptionBold: adContent.description_bold || false,
  descriptionItalic: adContent.description_italic || false,
  descriptionShadow: adContent.description_shadow || false,

  ctaFontSize: parseFloat(adContent.cta_font_size) || 16,
  ctaColor: adContent.cta_color,
  ctaAlign: adContent.cta_align || 'center',
  ctaBold: adContent.cta_bold || false,
  ctaItalic: adContent.cta_italic || false,
  ctaShadow: adContent.cta_shadow || false,
});

// Helper used in CampaignBuilder to provide consistent overlay styles per framework.
// Copied here to ensure public preview matches builder visuals exactly.
const getImageOverlayStyles = (framework: string) => {
  switch (framework) {
    case 'overlay-classic':
    case 'vertical-flow':
    case 'left-split':
    case 'headline-impact':
    case 'quote-bubble':
    case 'full-visual':
      return {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '0.375rem'
      };
    default:
      return {};
  }
};

// Helper: convert hex color to rgba with specified opacity
const hexToRgba = (hex: string, opacity: number = 1): string => {
  if (!hex) return `rgba(0, 102, 255, ${opacity})`; // Default to brand blue

  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex values
  let r: number, g: number, b: number;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  } else {
    return `rgba(0, 102, 255, ${opacity})`;
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Helper function to ensure font size has 'px' suffix.
// Use canonical logical canvas (640px base) for layout parity with the editor.
// Public preview will obtain higher pixel density via the capture worker (deviceScaleFactor)
// instead of changing CSS layout values.
const CANVAS_SCALE_FACTOR = 1; // Keep CSS layout values 1:1 with builder (no CSS upscaling)

const ensureFontSizePx = (size: string | number | undefined, fallback: string): string => {
  if (size === undefined || size === null || size === '') {
    const fallbackNum = parseFloat(fallback);
    if (!isNaN(fallbackNum)) return `${Math.round(fallbackNum * CANVAS_SCALE_FACTOR)}px`;
    return fallback;
  }
  if (typeof size === 'string' && size.endsWith('px')) {
    const numValue = parseFloat(size);
    if (!isNaN(numValue)) return `${Math.round(numValue * CANVAS_SCALE_FACTOR)}px`;
    return size;
  }
  const numValue = typeof size === 'number' ? size : parseFloat(size);
  if (!isNaN(numValue)) return `${Math.round(numValue * CANVAS_SCALE_FACTOR)}px`;
  return fallback;
};

// Zone A Canvas component that matches the exact structure from campaign builder
const ZoneACanvas = ({ campaign }: { campaign: any }) => {
  // Extract ad_content data first, then fallback to campaign data for backward compatibility
  const adContent = campaign.ad_content || {};

  // FIXED 4K canvas dimensions (1920x2160) for high-resolution output
  // This ensures Playwright screenshots are crisp on 4K displays
  // The 8:9 aspect ratio is maintained (1920/2160 = 8/9)
  // Font sizes are scaled 3x from the builder values (640→1920)

  // Extract positioning coordinates separately (percentage-based) - convert strings to numbers
  const headline_x = parseFloat(adContent.headline_x) || 50;
  const headline_y = parseFloat(adContent.headline_y) || 70;
  const benefit_x = parseFloat(adContent.benefit_x) || 50;
  const benefit_y = parseFloat(adContent.benefit_y) || 60;
  const description_x = parseFloat(adContent.description_x) || 50;
  const description_y = parseFloat(adContent.description_y) || 50;
  const cta_x = parseFloat(adContent.cta_x) || 50;
  const cta_y = parseFloat(adContent.cta_y) || 30;

  // Extract comprehensive layout data from ad_content table
  const {
    // Text content from ad_content with fallback to campaign
    headline = adContent.headline || campaign.headline || '',
    benefit = adContent.benefit || campaign.benefit || '',
    description = adContent.description || campaign.description || '',
    call_to_action = adContent.call_to_action || campaign.call_to_action || 'Learn More',

    // Visual design from ad_content with fallback to campaign
    primary_color = adContent.primary_color || campaign.primary_color || '#1E40AF',
    secondary_color = adContent.secondary_color || campaign.secondary_color || '#3B82F6',
    accent_color = adContent.accent_color || campaign.accent_color || '#60A5FA',
    selectedLayoutStyle = adContent.selected_layout_style || campaign.selectedLayoutStyle || 'overlay-classic',

    // Media fields from ad_content
    // NOTE: Do NOT read campaign.ad_image_url or campaign.final_render_url here — these fields store
    // screenshot outputs and cause duplicate renders when the media step is saved multiple times.
    // Only read ad_content.preview_url (if present) or campaign.preview_url when deciding what to show.
    preview_url_from_adcontent = adContent.ad_image_url,
    overlay_image_url = adContent.overlay_image_url,
    logo_url = adContent.logo_url,

    // Overlay and logo positioning data
    overlay_position = adContent.overlay_position || {},
    overlay_size = adContent.overlay_size || 25,
    logo_position = adContent.logo_position || {},
    logo_size = adContent.logo_size || 20,

    // Typography settings (with fallbacks to ensure values exist)
    // Use ensureFontSizePx to handle both number (e.g., 36) and string (e.g., '36px') formats from database
    headline_font_size = ensureFontSizePx(adContent.headline_font_size, '36px'),
    benefit_font_size = ensureFontSizePx(adContent.benefit_font_size, '18px'),
    description_font_size = ensureFontSizePx(adContent.description_font_size, '16px'),
    cta_font_size = ensureFontSizePx(adContent.cta_font_size, '16px'),
    headline_font_weight = adContent.headline_font_weight || 'bold',
    benefit_font_weight = adContent.benefit_font_weight || 'normal',
    description_font_weight = adContent.description_font_weight || 'normal',
    cta_font_weight = adContent.cta_font_weight || 'bold',

    // Typography alignments
    headline_align = adContent.headline_align || 'center',
    benefit_align = adContent.benefit_align || 'center',
    description_align = adContent.description_align || 'center',
    cta_align = adContent.cta_align || 'center',

    // Typography styles (boolean values from campaign builder)
    headline_bold = adContent.headline_bold || false,
    benefit_bold = adContent.benefit_bold || false,
    description_bold = adContent.description_bold || false,
    cta_bold = adContent.cta_bold || false,

    headline_italic = adContent.headline_italic || false,
    benefit_italic = adContent.benefit_italic || false,
    description_italic = adContent.description_italic || false,
    cta_italic = adContent.cta_italic || false,

    headline_shadow = adContent.headline_shadow || false,
    benefit_shadow = adContent.benefit_shadow || false,
    description_shadow = adContent.description_shadow || false,
    cta_shadow = adContent.cta_shadow || false,
    headline_font_style = adContent.headline_font_style || 'normal',
    benefit_font_style = adContent.benefit_font_style || 'normal',
    description_font_style = adContent.description_font_style || 'normal',
    cta_font_style = adContent.cta_font_style || 'normal',
    // Font family: prioritize individual settings, then selected_font (the main font selector), then fallback
    headline_font_family = adContent.headline_font_family || adContent.selected_font || 'Inter, sans-serif',
    benefit_font_family = adContent.benefit_font_family || adContent.selected_font || 'Inter, sans-serif',
    description_font_family = adContent.description_font_family || adContent.selected_font || 'Inter, sans-serif',
    cta_font_family = adContent.cta_font_family || adContent.selected_font || 'Inter, sans-serif',

    // Colors
    headline_color = adContent.headline_color || '#E8F3FF',
    benefit_color = adContent.benefit_color || '#E8F3FF',
    description_color = adContent.description_color || '#E8F3FF',
    cta_color = adContent.cta_color || '#FFFFFF',
    // CTA background: use cta_background_color if explicitly set, otherwise fall back to accent_color
    cta_background_color = adContent.cta_background_color || adContent.accent_color || campaign.accent_color || '#60A5FA',

    // Text effects
    headline_text_shadow = adContent.headline_text_shadow || 'none',
    benefit_text_shadow = adContent.benefit_text_shadow || 'none',
    description_text_shadow = adContent.description_text_shadow || 'none',
    cta_text_shadow = adContent.cta_text_shadow || 'none',

    // Special effects
    special_effects = adContent.special_effects || {},
    specialEffect = adContent.special_effect || 'none',
    blurIntensity = adContent.blur_intensity || 15,

    // Overlay settings
    overlay_opacity = adContent.overlay_opacity || null,
    overlay_blur = adContent.overlay_blur || null,
    // Auto-enhance: whether to apply client-side brightness/contrast/saturate filters
    auto_enhance = (adContent.auto_enhance !== undefined ? adContent.auto_enhance : campaign.auto_enhance) || false,
  } = { ...campaign, ...adContent };

  // Build typography object for getTypographyStyle (matching Step 4 structure)
  const typography = buildTypographyObject(adContent);

  // Transform properties - calculate from coordinates instead of using database values
  const headline_transform = `translate(${(headline_x - 50) * 2}%, ${(headline_y - 50) * 2}%)`;
  const benefit_transform = `translate(${(benefit_x - 50) * 2}%, ${(benefit_y - 50) * 2}%)`;
  const description_transform = `translate(${(description_x - 50) * 2}%, ${(description_y - 50) * 2}%)`;
  const cta_transform = `translate(${(cta_x - 50) * 2}%, ${(cta_y - 50) * 2}%)`;

  // Check if URLs are blob URLs (not accessible by Playwright)
  const isBlobUrl = (url: string) => url && url.startsWith('blob:');
  const isOverlayAccessible = overlay_image_url && !isBlobUrl(overlay_image_url);
  const isLogoAccessible = logo_url && !isBlobUrl(logo_url);

  // Helper function for valid backdrop blur classes
  const getValidBackdropBlurClass = (blurValue: string): string => {
    const blurMap: { [key: string]: string } = {
      'none': 'backdrop-blur-none',
      'sm': 'backdrop-blur-sm',
      'md': 'backdrop-blur-md',
      'lg': 'backdrop-blur-lg',
      'xl': 'backdrop-blur-xl'
    };
    return blurMap[blurValue] || '';
  };

  // Helper functions to convert boolean values to CSS values
  const getFontWeight = (isBold: boolean, fallback: string): string => {
    return isBold ? 'bold' : fallback;
  };

  const getFontStyle = (isItalic: boolean, fallback: string): string => {
    return isItalic ? 'italic' : fallback;
  };

  // Scale pixel values for 4K output
  const scalePx = (value: number): string => `${value * CANVAS_SCALE_FACTOR}px`;

  const getTextShadow = (hasShadow: boolean, fallback: string): string => {
    if (hasShadow) {
      // Scale shadow for 4K: 2px 2px 4px → 6px 6px 12px
      return `${scalePx(2)} ${scalePx(2)} ${scalePx(4)} rgba(0, 0, 0, 0.5)`;
    }
    return fallback;
  };

  // Determine which image URL to use
  // Only use preview URLs — do NOT use final_render_url or ad_image_url stored at campaign level.
  const adImageUrl = preview_url_from_adcontent;
  const adImage = !!adImageUrl;
  const useBackgroundOnly = adContent.useBackgroundOnly || campaign.useBackgroundOnly || adContent.use_background_only || campaign.use_background_only || false;

  return (
    <div
      className="bg-white rounded-xl shadow-lg overflow-hidden mx-auto relative flex-shrink-0"
      data-zone-a="true"
      style={{
        // Canonical logical canvas dimensions to match the editor (8:9 aspect ratio).
        // Keep CSS layout identical to builder (640x720). The screenshot worker will
        // request a higher deviceScaleFactor (DPR) to generate high-resolution bitmaps.
        width: '640px',
        height: '720px',
        boxSizing: 'border-box'
      }}
    >
      {/* Ad Canvas - 8:9 Aspect Ratio */}
      <div
        data-ad-canvas="true"
        className={`relative w-full h-full overflow-hidden min-h-0 min-w-0 ${special_effects?.border_glow ? 'ring-4' : ''}`}
        style={{
        // Match AdContentMediaStep.tsx logic:
        // - If useBackgroundOnly is true OR no image: use brand color
        // - If useBackgroundOnly is false AND no image: use white background
        // - If image exists: let image be the background
        backgroundColor: (useBackgroundOnly || !adImage) ? (useBackgroundOnly ? (primary_color || '#0066FF') : '#FFFFFF') : undefined,
        backgroundImage: (!useBackgroundOnly && adImage && adImageUrl) ? `url(${adImageUrl})` : (adImage && adImageUrl ? `url(${adImageUrl})` : undefined),
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          fontFamily: headline_font_family || 'Inter, sans-serif',
          borderRadius: '0.75rem',
          ...(special_effects?.border_glow && {
            boxShadow: special_effects.border_glow,
            borderColor: special_effects.border_color || 'transparent',
            borderWidth: special_effects.border_width || '0px'
          }),
          ...(special_effects?.shadow && {
            boxShadow: special_effects.shadow
          }),
        ...(special_effects?.backdrop_blur && {
          backdropFilter: `blur(${special_effects.backdrop_blur}px)`
        }),
        // Apply same auto-enhance filter as builder when an image background exists
        filter: (auto_enhance && adImage) ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none'
        }}
      >
        {/* Overlay Image - positioned over background but under text (only if accessible URL) */}
        {isOverlayAccessible && (
          <div
            className="absolute"
            style={{
              left: `${overlay_position.x || 50}%`,
              top: `${overlay_position.y || 50}%`,
              width: `${overlay_size}%`,
              height: 'auto',
              transform: 'translate(-50%, -50%)',
              maxWidth: scalePx(800), // 2400px for 4K
              maxHeight: scalePx(800), // 2400px for 4K
              zIndex: 15,
            }}
          >
            <img
              src={overlay_image_url}
              alt="Overlay"
              className="w-full h-auto object-contain"
              style={{ filter: auto_enhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none' }}
            />
          </div>
        )}

        {/* Brand Logo - positioned using logo position controls (only if accessible URL) */}
        {isLogoAccessible && (
          <div
            className="absolute"
            style={{
              left: `${logo_position.x || 50}%`,
              top: `${logo_position.y || 50}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 16,
              width: `${logo_size}%`,
            }}
          >
            <img
              src={logo_url}
              alt="Brand Logo"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* Layout based on selected visual style - EXACT COPY from AdContentMediaStep.tsx */}
        {selectedLayoutStyle === 'vertical-flow' && (
          <div className="relative h-full flex flex-col min-h-0">
            <div className="rounded flex items-center justify-center overflow-hidden relative flex-shrink" style={{ minHeight: '40%', maxHeight: '66.67%', flex: '1 1 auto' }}>

              {/* Special Effect: Blur Background */}
              {specialEffect === 'blur' && adImage && (
                <div className="absolute inset-0 z-10" style={{
                  backgroundImage: `url(${adImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: `blur(${blurIntensity}px) brightness(1.1)`,
                  transform: 'scale(1.1)',
                }}></div>
              )}

              {/* Special Effect: Gradient Overlay */}
              {specialEffect === 'gradient' && (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 z-10"></div>
              )}

              {/* Special Effect: Additional Shadow Overlay for Drop Shadow */}
              {specialEffect === 'shadow' && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              )}

              {/* Standard Background Overlay (when not using blur effect) */}
              {specialEffect !== 'blur' && (
                <div className="absolute inset-0 bg-opacity-30 z-10"></div>
              )}

              {/* Blur Effect Overlay (lighter for better text readability) */}
              {specialEffect === 'blur' && (
                <div className="absolute inset-0 bg-black bg-opacity-20 z-10"></div>
              )}
              {adImage ? (
                <>
                  <img
                    src={adImageUrl || ''}
                    alt="Ad Content"
                    className="w-full h-full object-cover rounded"
                    style={{
                      filter: auto_enhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none'
                    }}
                  />
                  {/* Use consistent overlay approach */}
                  <div style={getImageOverlayStyles('vertical-flow')}></div>
                </>
              ) : (
                <div
                  className="w-full h-full rounded"
                  style={
                    useBackgroundOnly ? { backgroundColor: primary_color || '#0066FF' } : { backgroundColor: '#FFFFFF' }
                  }
                />
              )}
            </div>
            <div
              id="text-content"
              className="flex flex-col p-2 z-20 flex-grow"
              style={{
                backgroundColor: primary_color || '#0066FF',
                minHeight: '33.33%'
              }}
            >
              <h1
                className={`${headline_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  fontWeight: headline_bold ? '700' : (getTypographyStyle('headline', typography, 1).fontWeight || '600'),
                  lineHeight: '1.2',
                  textAlign: headline_align,
                  color: headline_color || getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  textShadow: headline_shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                  transform: headline_transform,
                  userSelect: 'none',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {headline}
              </h1>

              <p
                className={`mb-1 ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  lineHeight: '1.3',
                  textAlign: benefit_align,
                  color: benefit_color || getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                  transform: benefit_transform,
                  userSelect: 'none',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {benefit}
              </p>

              <p
                className={`mb-2 opacity-90 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  lineHeight: '1.2',
                  textAlign: description_align,
                  color: description_color || getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                  transform: description_transform,
                  userSelect: 'none',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {description}
              </p>

              <p
                className={`mb-6 ${cta_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  fontWeight: cta_bold ? '700' : (getTypographyStyle('cta', typography, 1).fontWeight || '400'),
                  lineHeight: '1.5',
                  textAlign: cta_align,
                  textShadow: cta_shadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none',
                  transform: cta_transform,
                  userSelect: 'none',
                  transformOrigin: 'center'
                }}
              >
                <button
                  className={`px-6 py-3 rounded-lg shadow-lg ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                    }`}
                  style={{
                    backgroundColor: accent_color,
                    ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    fontWeight: cta_bold ? '700' : (getTypographyStyle('cta', typography, 1).fontWeight || '600'),
                    textShadow: (cta_shadow) ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                    userSelect: 'none'
                  }}
                >
                  {call_to_action}
                </button>
              </p>
            </div>
          </div>
        )}

        {selectedLayoutStyle === 'left-split' && (
          <div className="relative flex h-full min-h-0">
            {/* Left Side - Visual/Image Area */}
            <div className="w-1/2 flex items-center justify-center relative overflow-hidden">
              {/* Special Effect: Blur Background */}
              {specialEffect === 'blur' && adImage && (
                <div className="absolute inset-0 z-10" style={{
                  backgroundImage: `url(${adImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: `blur(${blurIntensity}px) brightness(1.1)`,
                  transform: 'scale(1.1)',
                }}></div>
              )}

              {/* Special Effect: Gradient Overlay */}
              {specialEffect === 'gradient' && (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 z-10"></div>
              )}

              {/* Special Effect: Additional Shadow Overlay for Drop Shadow */}
              {specialEffect === 'shadow' && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              )}

              {/* Standard Background Overlay (when not using blur effect) */}
              {specialEffect !== 'blur' && (
                <div className="absolute inset-0 bg-opacity-30 z-10"></div>
              )}

              {/* Blur Effect Overlay (lighter for better text readability) */}
              {specialEffect === 'blur' && (
                <div className="absolute inset-0 bg-black bg-opacity-20 z-10"></div>
              )}
              {adImage ? (
                <>
                  <img
                    src={adImageUrl || ''}
                    alt="Ad Content"
                    className="w-full h-full object-cover"
                    style={{
                      filter: auto_enhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none'
                    }}
                  />
                  {/* Use consistent overlay approach */}
                  <div style={getImageOverlayStyles('left-split')}></div>
                </>
              ) : (
                <div
                  className="w-full h-full z-0"
                  style={
                    useBackgroundOnly ? { backgroundColor: primary_color || '#0066FF' } : { backgroundColor: '#FFFFFF' }
                  }
                />
              )}

              <div className="text-center relative z-10">
                {/* Brand logo area - can be added here if needed */}
              </div>
            </div>

            {/* Right Side - Text Content */}
            <div
              className="w-1/2 flex flex-col justify-center p-4 relative"
              style={{
                backgroundColor: primary_color,
                zIndex: 20
              }}
            >
              <h1
                className={`mb-3 ${headline_bold ? 'font-bold' : 'font-semibold'} ${headline_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('headline', typography, 0.9, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  lineHeight: '1.1',
                  textShadow: headline_shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                  color: headline_color || getTypographyStyle('headline', typography, 0.9, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  transform: headline_transform,
                  userSelect: 'none',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                }}
              >
                {headline}
              </h1>

              <p
                className={`mb-2 ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('benefit', typography, 0.8, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  lineHeight: '1.3',
                  textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                  color: benefit_color || getTypographyStyle('benefit', typography, 0.8, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  opacity: 0.95,
                  transform: benefit_transform,
                  userSelect: 'none',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                }}
              >
                {benefit}
              </p>

              <p
                className={`mb-4 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                style={{
                  ...getTypographyStyle('description', typography, 0.7, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                  lineHeight: '1.3',
                  textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                  color: description_color || getTypographyStyle('description', typography, 0.7, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                  opacity: 0.9,
                  transform: description_transform,
                  userSelect: 'none',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                }}
              >
                {description}
              </p>

              <div className={`flex ${headline_align === 'left' ? 'justify-start' :
                  headline_align === 'right' ? 'justify-end' :
                    headline_align === 'justify' ? 'justify-center' :
                      'justify-center'
                }`} style={{
                  transform: cta_transform,
                  transformOrigin: 'center'
                }}>
                <button
                  className={`px-4 py-2 rounded-lg shadow-lg ${cta_bold ? 'font-bold' : 'font-semibold'} ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                    }`}
                  style={{
                    backgroundColor: accent_color,
                    ...getTypographyStyle('cta', typography, 0.8, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    textShadow: cta_shadow ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                    color: cta_color || getTypographyStyle('cta', typography, 0.8, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                    border: '2px solid rgba(255,255,255,0.2)',
                    userSelect: 'none'
                  }}
                >
                  {call_to_action}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedLayoutStyle === 'overlay-classic' && (
          <div className="relative flex items-end h-full p-8 min-h-0">
            <AdVisualBackground
              adImage={adImageUrl}
              adImageUrl={adImageUrl}
              autoEnhance={auto_enhance}
              specialEffect={specialEffect}
              blurIntensity={blurIntensity}
              primaryColor={primary_color}
              useBackgroundOnly={useBackgroundOnly}
              getImageOverlayStyles={getImageOverlayStyles}
              styleKey={selectedLayoutStyle}
            />

            <div className="w-full z-20">
              {/* Text Content at Bottom */}
              <div className={`rounded-xl p-6 ${blurIntensity && blurIntensity > 0 ? `backdrop-blur-${blurIntensity === 'low' ? 'sm' : blurIntensity === 'medium' ? 'md' : blurIntensity === 'high' ? 'lg' : 'xl'}` : ''}`} style={{
                ...(blurIntensity && blurIntensity > 0 ? { backgroundColor: 'rgba(0, 0, 0, 0.4)' } : { backgroundColor: primary_color })
              }}>
                <h1
                  className={`mb-3 ${headline_bold ? 'font-bold' : 'font-semibold'} ${headline_italic ? 'italic' : ''}`}
                  style={{
                    ...getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.2',
                    textShadow: headline_shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                    transform: headline_transform,
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%'
                  }}
                >
                  {headline}
                </h1>

                <p
                  className={`mb-2 ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                  style={{
                    ...getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.5',
                    textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                    transform: benefit_transform,
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%'
                  }}
                >
                  {benefit}
                </p>

                <p
                  className={`mb-4 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                  style={{
                    ...getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.4',
                    textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                    transform: description_transform,
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%'
                  }}
                >
                  {description}
                </p>

                <div
                  className={`w-full`}
                  style={{
                    transform: cta_transform,
                    transformOrigin: 'center'
                  }}
                >
                  <div className={`flex ${cta_align === 'left' ? 'justify-start' :
                    cta_align === 'right' ? 'justify-end' :
                      cta_align === 'justify' ? 'justify-center' :
                        'justify-center'
                  }`}>
                    <button
                      className={`px-6 py-3 rounded-lg shadow-lg ${cta_bold ? 'font-bold' : 'font-semibold'} ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                        }`}
                      style={{
                        backgroundColor: accent_color,
                        ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                        userSelect: 'none'
                      }}
                    >
                      {call_to_action}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedLayoutStyle === 'headline-impact' && (
          <div className="relative flex flex-col justify-center h-full p-8 min-h-0">
            {/* Background Image */}
            {adImage ? (
              <AdVisualBackground
                adImage={adImageUrl}
                adImageUrl={adImageUrl}
                autoEnhance={auto_enhance}
                specialEffect={specialEffect}
                blurIntensity={blurIntensity}
                primaryColor={primary_color}
                useBackgroundOnly={useBackgroundOnly}
                getImageOverlayStyles={getImageOverlayStyles}
                styleKey={selectedLayoutStyle}
              />
            ) : (
              <div
                className="absolute inset-0 rounded-xl"
                style={ useBackgroundOnly ? { backgroundColor: primary_color || '#0066FF' } : { backgroundColor: '#FFFFFF' } }
              />
            )}

            {/* Overlay to cover background image with primary color for headline-impact */}
            {/* Only apply overlay when not using white background */}
            {!useBackgroundOnly && (
              <div
                aria-hidden
                className="absolute inset-0 z-10"
                style={{ backgroundColor: primary_color || '#0066FF', opacity: 0.65 }}
              />
            )}

            <div className="max-w-2xl mx-auto w-full z-20">
              {/* Large Impact Headline - apply per-element transform so X/Y sliders work */}
              <div
                style={{
                  width: '100%',
                  transform: headline_transform,
                  transformOrigin: 'center',
                  textAlign: headline_align === 'justify' ? 'center' : (headline_align as 'left' | 'center' | 'right')
                }}
              >
                <h1
                  className={`mb-8 ${headline_bold ? 'font-bold' : 'font-semibold'} ${headline_italic ? 'italic' : ''}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    ...getTypographyStyle('headline', typography, 1.3, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    // Use the user-controlled headlineFontSize state so the slider affects preview.
                    // Keep the same visual scale (1.3) applied in getTypographyStyle, but derive size from headlineFontSize.
                    fontSize: `${Math.max(8, Math.round(typography.headlineFontSize * 1.3))}px`,
                    lineHeight: '1.1',
                    textAlign: headline_align,
                    textShadow: headline_shadow ? '3px 3px 6px rgba(0,0,0,0.7)' : 'none',
                    userSelect: 'none',
                    // prevent the headline from forcing the container to grow
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    maxHeight: '70vh'
                  }}
                >
                  {headline}
                </h1>
              </div>

              {/* Smaller supporting text */}
              <div
                style={{
                  width: '100%',
                  transform: benefit_transform,
                  transformOrigin: 'center',
                  textAlign: benefit_align === 'justify' ? 'center' : (benefit_align as 'left' | 'center' | 'right')
                }}
              >
                <p
                  className={`mb-6 ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    ...getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.5',
                    textAlign: benefit_align,
                    textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none',
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%'
                  }}
                >
                  {benefit}
                </p>
              </div>

              {/* Description - shows placeholder when empty */}
              <div
                style={{
                  width: '100%',
                  transform: description_transform,
                  transformOrigin: 'center',
                  textAlign: description_align === 'justify' ? 'center' : (description_align as 'left' | 'center' | 'right')
                }}
              >
                <p
                  className={`mb-6 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    ...getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.4',
                    textAlign: description_align,
                    textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none',
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  {description}
                </p>
              </div>

              {/* CTA Button - apply transform separately so X/Y sliders affect it */}
              <div
                className={`flex ${cta_align === 'left' ? 'justify-start' :
                  cta_align === 'right' ? 'justify-end' :
                    cta_align === 'justify' ? 'justify-center' :
                      'justify-center'
                } w-full`}
                style={{
                  transform: cta_transform,
                  transformOrigin: 'center',
                  textAlign: cta_align === 'justify' ? 'center' : (cta_align as 'left' | 'center' | 'right')
                }}
              >
                <button
                  className={`px-8 py-4 rounded-lg shadow-lg ${cta_bold ? 'font-bold' : 'font-semibold'} ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                    }`}
                  style={{
                    display: 'inline-block',
                    backgroundColor: accent_color,
                    ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    textShadow: cta_shadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                    userSelect: 'none'
                  }}
                >
                  {call_to_action}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedLayoutStyle === 'quote-bubble' && (
          <div className="relative flex flex-col justify-center h-full p-8 min-h-0">
            {/* Background Image */}
            <AdVisualBackground
              adImage={adImageUrl}
              adImageUrl={adImageUrl}
              specialEffect={specialEffect}
              blurIntensity={blurIntensity}
              primaryColor={primary_color}
              useBackgroundOnly={useBackgroundOnly}
              getImageOverlayStyles={getImageOverlayStyles}
              styleKey={selectedLayoutStyle}
            />

            <div className="w-full max-w-lg mx-auto z-20">
              {/* Quote Bubble */}
              <div
                className={`rounded-2xl p-6 mb-6 shadow-lg relative w-full min-w-0 ${blurIntensity && blurIntensity > 0 ? `backdrop-blur-${blurIntensity === 'low' ? 'sm' : blurIntensity === 'medium' ? 'md' : blurIntensity === 'high' ? 'lg' : 'xl'}` : ''}`}
                style={{
                  backgroundColor: useBackgroundOnly ? '#FFFFFF' : hexToRgba(primary_color || '#0066FF', 0.85)
                }}
              >
                {/* Speech bubble tail - wrapper positioned OUTSIDE bubble to avoid overlap */}
                <div
                  className="absolute left-8 overflow-hidden"
                  style={{
                    width: '24px',
                    height: '17px', // Half of diamond height (~34px / 2 = 17px)
                    bottom: '-15px', // Moved up to close gap
                    left: '2rem' // left-8 = 2rem
                  }}
                >
                  <div
                    className="w-6 h-6 transform rotate-45"
                    style={{
                      backgroundColor: useBackgroundOnly ? '#FFFFFF' : hexToRgba(primary_color || '#0066FF', 0.85),
                      position: 'absolute',
                      top: '-17px' // Diamond center aligns with bubble edge
                    }}
                  ></div>
                </div>

                {/* Headline */}
                <div
                  style={{
                    width: '100%',
                    transform: headline_transform,
                    transformOrigin: 'center',
                    textAlign: headline_align === 'justify' ? 'center' : (headline_align as 'left' | 'center' | 'right')
                  }}
                >
                  <h1
                    className={`mb-4 ${headline_bold ? 'font-bold' : 'font-semibold'} ${headline_italic ? 'italic' : ''}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      ...getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      lineHeight: '1.2',
                      textAlign: headline_align,
                      color: headline_color || getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                      textShadow: headline_shadow ? '2px 2px 4px rgba(0,0,0,0.7)' : 'none',
                      userSelect: 'none'
                    }}
                  >
                    {headline}
                  </h1>
                </div>

                {/* Benefit */}
                <div
                  style={{
                    width: '100%',
                    transform: benefit_transform,
                    transformOrigin: 'center',
                    textAlign: benefit_align === 'justify' ? 'center' : (benefit_align as 'left' | 'center' | 'right')
                  }}
                >
                  <p
                    className={`mb-4 ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      ...getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      lineHeight: '1.4',
                      textAlign: benefit_align,
                      color: benefit_color || getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                      textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                      userSelect: 'none'
                    }}
                  >
                    {benefit}
                  </p>
                </div>

                {/* Description */}
                <div
                  style={{
                    width: '100%',
                    transform: description_transform,
                    transformOrigin: 'center',
                    textAlign: description_align === 'justify' ? 'center' : (description_align as 'left' | 'center' | 'right')
                  }}
                >
                  <p
                    className={`mb-2 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      ...getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      lineHeight: '1.4',
                      textAlign: description_align,
                      color: description_color || getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }).color,
                      textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none',
                      userSelect: 'none'
                    }}
                  >
                    {description}
                  </p>
                </div>
              </div>

              {/* CTA Button - Outside the bubble */}
              <div
                className={`flex ${cta_align === 'left' ? 'justify-start' :
                  cta_align === 'right' ? 'justify-end' :
                    cta_align === 'justify' ? 'justify-center' :
                      'justify-center'
                }`}
                style={{
                  transform: cta_transform,
                  transformOrigin: 'center'
                }}
              >
                <button
                  className={`px-8 py-4 rounded-lg shadow-lg ${cta_bold ? 'font-bold' : 'font-semibold'} ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                    }`}
                  style={{
                    backgroundColor: accent_color,
                    ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    fontWeight: cta_bold ? '700' : (getTypographyStyle('cta', typography, 1).fontWeight || '600'),
                    textShadow: cta_shadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                    userSelect: 'none'
                  }}
                >
                  {call_to_action}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedLayoutStyle === 'full-visual' && (
          <div className="relative h-full min-h-0">
            {/* Background Image */}
            {adImage ? (
              <AdVisualBackground
                adImage={adImageUrl}
                adImageUrl={adImageUrl}
                autoEnhance={auto_enhance}
                specialEffect={specialEffect}
                blurIntensity={blurIntensity}
                useBackgroundOnly={useBackgroundOnly}
                primaryColor={primary_color}
                getImageOverlayStyles={getImageOverlayStyles}
                styleKey={selectedLayoutStyle}
              />
            ) : (
              <div
                className="absolute inset-0 rounded-xl"
                style={ useBackgroundOnly ? { backgroundColor: primary_color || '#0066FF' } : { backgroundColor: '#FFFFFF' } }
              />
            )}

            {/* Top positioning: Headline - position based on alignment */}
            <div className={`absolute top-4 z-20 ${
              headline_align === 'center' ? 'left-1/2 -translate-x-1/2' :
              headline_align === 'right' ? 'right-4' :
              'left-4'
            }`}>
              <div
                style={{
                  width: '100%',
                  transform: headline_transform,
                  transformOrigin: 'center',
                  // apply alignment directly so changes take effect
                  textAlign: headline_align
                }}
              >
                <h1
                  id="ad-headline"
                  className={`font-black text-white ${headline_bold ? 'font-bold' : 'font-black'} ${headline_italic ? 'italic' : ''}`}
                  style={{
                    ...getTypographyStyle('headline', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                    lineHeight: '1.1',
                    textShadow: headline_shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                    color: headline_color || '#FFFFFF',
                    // also set on the element to be robust against parent/stylesheet overrides
                    textAlign: headline_align,
                    userSelect: 'none',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%'
                  }}
                >
                  {headline}
                </h1>
              </div>
            </div>

            {/* Bottom bar: left (description/benefit) and right (CTA) - keeps them from overlapping */}
            <div id="ad-bottom-content" className="absolute bottom-4 left-4 right-4 flex items-end z-20">
              <div id="ad-benefit-description-container" className="max-w-xs pr-6" style={{ width: '320px', minWidth: '320px', flexShrink: 0 }}>
                {/* Key Benefit above (swap order so DOM matches renderer expectation) */}
                <div
                  style={{
                    width: '100%',
                    transform: benefit_transform,
                    transformOrigin: 'center',
                    textAlign: benefit_align === 'justify' ? 'center' : (benefit_align as 'left' | 'center' | 'right')
                  }}
                >
                  <p
                    id="ad-benefit"
                    className={`text-white ${benefit_bold ? 'font-bold' : ''} ${benefit_italic ? 'italic' : ''}`}
                    style={{
                      ...getTypographyStyle('benefit', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      lineHeight: '1.3',
                      textShadow: benefit_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                      color: benefit_color || '#FFFFFF',
                      userSelect: 'none',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      maxWidth: '100%'
                    }}
                  >
                    {benefit}
                  </p>
                </div>

                {/* Description below */}
                <div
                  style={{
                    width: '100%',
                    transform: description_transform,
                    transformOrigin: 'center',
                    textAlign: description_align === 'justify' ? 'center' : (description_align as 'left' | 'center' | 'right')
                  }}
                >
                  <p
                    id="ad-description"
                    className={`text-white mb-2 ${description_bold ? 'font-bold' : ''} ${description_italic ? 'italic' : ''}`}
                    style={{
                      ...getTypographyStyle('description', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      lineHeight: '1.3',
                      textShadow: description_shadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none',
                      color: description_color || '#FFFFFF',
                      userSelect: 'none',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      maxWidth: '100%'
                    }}
                  >
                    {description}
                  </p>
                </div>
              </div>

              <div id="ad-cta-container" className="ml-auto">
                <div
                  className={`w-full flex ${cta_align === 'left' ? 'justify-start' :
                    cta_align === 'right' ? 'justify-end' :
                      cta_align === 'justify' ? 'justify-center' :
                        'justify-center'
                  }`}
                  style={{
                    transform: cta_transform,
                    transformOrigin: 'center',
                    textAlign: cta_align === 'justify' ? 'center' : (cta_align as 'left' | 'center' | 'right')
                  }}
                >
                  <button
                    id="ad-cta"
                    className={`px-3 py-2 rounded font-bold ${cta_bold ? 'font-bold' : 'font-bold'} ${cta_italic ? 'italic' : ''} ${cta_align === 'justify' ? 'w-full' : ''
                      }`}
                    style={{
                      backgroundColor: accent_color || '#1F2937',
                      ...getTypographyStyle('cta', typography, 1, { primary: primary_color, secondary: secondary_color, accent: accent_color }),
                      color: cta_color || '#FFFFFF',
                      userSelect: 'none'
                    }}
                  >
                    {call_to_action}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fallback for unknown layout - simple centered text */}
        {!['vertical-flow', 'left-split', 'overlay-classic', 'headline-impact', 'quote-bubble', 'full-visual'].includes(selectedLayoutStyle) && (
          <div className="relative flex items-center justify-center h-full min-h-0">
            {adImage ? (
              <>
                <img
                  src={adImageUrl}
                  alt="Ad Content"
                  className="w-full h-full object-cover rounded"
                />

                      {/* Special Effect: Blur Background */}
                      {specialEffect === 'blur' && adImage && (
                        <div className="absolute inset-0 z-10" style={{
                          backgroundImage: `url(${adImageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: `blur(${blurIntensity}px) brightness(1.1)`,
                          transform: 'scale(1.1)',
                        }}></div>
                      )}

                      {/* Special Effect: Gradient Overlay */}
                      {specialEffect === 'gradient' && (
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 z-10"></div>
                      )}

                      {/* Special Effect: Additional Shadow Overlay for Drop Shadow */}
                      {specialEffect === 'shadow' && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                      )}

                      {/* Standard Background Overlay (when not using blur effect) */}
                      {specialEffect !== 'blur' && (
                        <div className="absolute inset-0 bg-opacity-30 z-10"></div>
                      )}

                      {/* Blur Effect Overlay (lighter for better text readability) */}
                      {specialEffect === 'blur' && (
                        <div className="absolute inset-0 bg-black bg-opacity-20 z-10"></div>
                      )}

                <div
                  className="absolute inset-0 bg-black"
                  style={{ opacity: overlay_opacity }}
                ></div>
              </>
            ) : (
              <div
                className="w-full h-full rounded"
                style={{ backgroundColor: primary_color }}
              />
            )}

            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-white z-10">
              {headline && (
                <h1
                  className="text-6xl font-bold mb-6 text-center"
                  style={{
                    color: headline_color,
                    fontSize: headline_font_size,
                    fontFamily: headline_font_family,
                    fontWeight: getFontWeight(headline_bold, headline_font_weight),
                    fontStyle: getFontStyle(headline_italic, headline_font_style),
                    textShadow: getTextShadow(headline_shadow, headline_text_shadow),
                    lineHeight: '1.2',
                  }}
                >
                  {headline}
                </h1>
              )}

              {description && (
                <p
                  className="text-2xl mb-8 text-center max-w-4xl"
                  style={{
                    color: description_color,
                    fontSize: description_font_size,
                    fontFamily: description_font_family,
                    fontWeight: getFontWeight(description_bold, description_font_weight),
                    fontStyle: getFontStyle(description_italic, description_font_style),
                    textShadow: getTextShadow(description_shadow, description_text_shadow),
                  }}
                >
                  {description}
                </p>
              )}

              {call_to_action && (
                <button
                  className="px-8 py-4 rounded-lg shadow-lg font-bold"
                  style={{
                    backgroundColor: cta_background_color,
                    color: cta_color,
                    fontSize: cta_font_size,
                    fontFamily: cta_font_family,
                    fontWeight: getFontWeight(cta_bold, cta_font_weight),
                    fontStyle: getFontStyle(cta_italic, cta_font_style),
                    textShadow: getTextShadow(cta_shadow, cta_text_shadow),
                  }}
                >
                  {call_to_action}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PublicCampaignPreview - Public preview page for Playwright screenshots
 *
 * This is a public version of CampaignPreview that doesn't require authentication.
 * It's designed to be visited by Playwright for server-side screenshot generation.
 *
 * Once the campaign is loaded and rendered, it sets window.__previewReady = true
 * which Playwright waits for before capturing the screenshot.
 */
const PublicCampaignPreview: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [campaign, setCampaign] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Signal to Playwright that the preview is ready
  useEffect(() => {
    if (campaign && !loading && !error) {
      setTimeout(() => {
        (window as any).__previewReady = true;
      }, 1000); // Give time for images and rendering
    }
  }, [campaign, loading, error]);

  useEffect(() => {
    const loadCampaign = async () => {
      if (!campaignId) {
        setError('No campaign ID provided');
        setLoading(false);
        return;
      }

      try {
        // Use public campaign service that doesn't require authentication
        const result = await publicCampaignService.getCampaignByIdPublic(campaignId);

        if (result.success && result.data) {
          setCampaign(result.data);
        } else {
          setError(result.error || 'Failed to load campaign');
          console.error('❌ Failed to load campaign publicly:', result.error);
        }
      } catch (err) {
        setError('Error loading campaign');
        console.error('❌ Error loading campaign publicly:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, [campaignId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign preview...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center text-red-600">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xl font-semibold mb-2">Preview Unavailable</p>
          <p className="text-gray-600">{error || 'Campaign not found'}</p>
        </div>
      </div>
    );
  }

  // Extract campaign data
  const {
    headline = '',
    benefit = '',
    description = '',
    call_to_action = 'Learn More',
    primary_color = '#1E40AF',
    secondary_color = '#3B82F6',
    accent_color = '#60A5FA',
    typography_settings,
    special_effects,
    // Use preview_url (Playwright preview) as canonical public preview image source.
    // Avoid using campaign.final_render_url or campaign.ad_image_url to prevent duplicate screenshot usage.
    preview_url,
    name,
  } = campaign;

  // Use preview_url (Playwright-rendered image) if available, show the rendered image
  // But if we have ad_content data, always show the ZoneACanvas instead
  if (preview_url && !campaign.ad_content) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
        <img
          src={preview_url}
          alt={`Preview of ${name}`}
          className="max-w-full max-h-screen object-contain"
          onLoad={() => {
            (window as any).__previewReady = true;
          }}
        />
      </div>
    );
  }

  // Otherwise, render the exact Zone A canvas structure (same as in campaign builder)
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
      <ZoneACanvas campaign={campaign} />
    </div>
  );
};

export default PublicCampaignPreview;